import React, { useState, useEffect } from 'react';
import { Link, useParams, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { Button } from '../components/ui/button';
import { 
  Play, 
  Clock, 
  Star,
  Bookmark,
  BookmarkCheck,
  ChevronLeft,
  Calendar,
  Users,
  Target,
  Dumbbell,
  Check,
  AlertCircle
} from 'lucide-react';
import { toast } from 'sonner';

const ClassDetail = () => {
  const { classId } = useParams();
  const { api, user } = useAuth();
  const navigate = useNavigate();
  const [classData, setClassData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [bookmarked, setBookmarked] = useState(false);
  const [booking, setBooking] = useState(false);

  useEffect(() => {
    const fetchClass = async () => {
      try {
        const response = await api.get(`/classes/${classId}`);
        setClassData(response.data);
        setBookmarked(user?.bookmarked_classes?.includes(classId) || false);
      } catch (error) {
        console.error('Failed to fetch class:', error);
        toast.error('Failed to load class details');
      } finally {
        setLoading(false);
      }
    };

    fetchClass();
  }, [api, classId, user]);

  const handleBookmark = async () => {
    try {
      const response = await api.post(`/classes/${classId}/bookmark`);
      setBookmarked(response.data.bookmarked);
      toast.success(response.data.bookmarked ? 'Class bookmarked!' : 'Bookmark removed');
    } catch (error) {
      toast.error('Failed to update bookmark');
    }
  };

  const handleBook = async () => {
    setBooking(true);
    try {
      await api.post(`/classes/${classId}/book`);
      toast.success('Class booked successfully!');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to book class');
    } finally {
      setBooking(false);
    }
  };

  const handleStartClass = () => {
    navigate(`/watch/${classId}`);
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[#050505] flex items-center justify-center">
        <div className="animate-pulse text-zinc-500">Loading...</div>
      </div>
    );
  }

  if (!classData) {
    return (
      <div className="min-h-screen bg-[#050505] flex flex-col items-center justify-center">
        <AlertCircle className="w-16 h-16 text-zinc-700 mb-4" />
        <h2 className="text-2xl font-bold mb-2">Class Not Found</h2>
        <p className="text-zinc-500 mb-6">The class you're looking for doesn't exist.</p>
        <Link to="/classes">
          <Button className="bg-white text-black">Browse Classes</Button>
        </Link>
      </div>
    );
  }

  const isLive = classData.class_type === 'live';
  const isScheduled = classData.status === 'scheduled';

  return (
    <div className="min-h-screen bg-[#050505]">
      {/* Hero Section */}
      <div className="relative h-[60vh] min-h-[400px]">
        <img 
          src={classData.thumbnail_url || 'https://images.unsplash.com/photo-1517836357463-d25dfeac3438?q=85&w=1920&auto=format&fit=crop'} 
          alt={classData.title}
          className="w-full h-full object-cover"
        />
        <div className="absolute inset-0 bg-gradient-to-t from-[#050505] via-[#050505]/60 to-transparent" />
        
        {/* Back Button */}
        <Link 
          to="/classes"
          className="absolute top-6 left-6 flex items-center gap-2 text-white/80 hover:text-white transition-colors z-10"
        >
          <ChevronLeft className="w-5 h-5" />
          Back to Classes
        </Link>

        {/* Bookmark Button */}
        <button
          onClick={handleBookmark}
          className="absolute top-6 right-6 w-10 h-10 bg-black/50 hover:bg-black/70 rounded-full flex items-center justify-center transition-colors z-10"
          data-testid="bookmark-btn"
        >
          {bookmarked ? (
            <BookmarkCheck className="w-5 h-5 text-teal-400" />
          ) : (
            <Bookmark className="w-5 h-5" />
          )}
        </button>

        {/* Class Info Overlay */}
        <div className="absolute bottom-0 left-0 right-0 p-6 md:p-12">
          <div className="max-w-4xl">
            {/* Badges */}
            <div className="flex flex-wrap items-center gap-3 mb-4">
              {isLive && isScheduled && (
                <span className="px-3 py-1 bg-red-500 text-white text-sm font-bold rounded-sm flex items-center gap-2">
                  <span className="w-2 h-2 bg-white rounded-full animate-pulse" />
                  LIVE
                </span>
              )}
              <span className={`px-3 py-1 rounded-sm text-sm font-medium badge-${classData.category}`}>
                {classData.category.replace('_', ' ')}
              </span>
              <span className={`px-3 py-1 bg-zinc-800 rounded-sm text-sm difficulty-${classData.difficulty}`}>
                {classData.difficulty}
              </span>
            </div>

            <h1 className="text-4xl md:text-5xl font-bold mb-4">{classData.title}</h1>

            <div className="flex flex-wrap items-center gap-6 text-zinc-400 mb-6">
              <div className="flex items-center gap-2">
                <Clock className="w-5 h-5" />
                <span>{classData.duration_minutes} minutes</span>
              </div>
              {classData.rating > 0 && (
                <div className="flex items-center gap-2">
                  <Star className="w-5 h-5 text-yellow-500 fill-yellow-500" />
                  <span className="text-white">{classData.rating.toFixed(1)}</span>
                  <span>({classData.total_ratings} reviews)</span>
                </div>
              )}
              {isLive && classData.scheduled_time && (
                <div className="flex items-center gap-2">
                  <Calendar className="w-5 h-5" />
                  <span>{new Date(classData.scheduled_time).toLocaleString()}</span>
                </div>
              )}
              {isLive && (
                <div className="flex items-center gap-2">
                  <Users className="w-5 h-5" />
                  <span>{classData.participant_count}/{classData.max_participants} spots</span>
                </div>
              )}
            </div>

            {/* Instructor */}
            <Link to={`/instructor/${classData.instructor_id}`} className="flex items-center gap-4 mb-8 group">
              <div className="w-12 h-12 bg-zinc-800 rounded-full overflow-hidden">
                <img 
                  src="https://images.unsplash.com/photo-1574230951449-2635f5cb3bc6?q=85&w=100&auto=format&fit=crop" 
                  alt={classData.instructor_name}
                  className="w-full h-full object-cover"
                />
              </div>
              <div>
                <p className="text-sm text-zinc-500">Instructor</p>
                <p className="font-bold group-hover:text-teal-400 transition-colors">{classData.instructor_name}</p>
              </div>
            </Link>

            {/* Action Buttons */}
            <div className="flex flex-wrap gap-4">
              {isLive && isScheduled ? (
                <Button
                  onClick={handleBook}
                  disabled={booking}
                  className="h-14 px-8 bg-white text-black font-bold uppercase tracking-wider hover:bg-zinc-200"
                  data-testid="book-class-btn"
                >
                  {booking ? 'Booking...' : 'Book This Class'}
                </Button>
              ) : (
                <Button
                  onClick={handleStartClass}
                  className="h-14 px-8 bg-white text-black font-bold uppercase tracking-wider hover:bg-zinc-200"
                  data-testid="start-class-btn"
                >
                  <Play className="w-5 h-5 mr-2 fill-black" />
                  Start Class
                </Button>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Class Details */}
      <div className="max-w-7xl mx-auto px-6 md:px-12 py-12">
        <div className="grid lg:grid-cols-3 gap-12">
          {/* Main Content */}
          <div className="lg:col-span-2 space-y-8">
            <section>
              <h2 className="text-2xl font-bold mb-4">ABOUT THIS CLASS</h2>
              <p className="text-zinc-400 leading-relaxed">{classData.description}</p>
            </section>

            {classData.focus_areas?.length > 0 && (
              <section>
                <h2 className="text-2xl font-bold mb-4">FOCUS AREAS</h2>
                <div className="flex flex-wrap gap-3">
                  {classData.focus_areas.map((area, index) => (
                    <span 
                      key={index}
                      className="flex items-center gap-2 px-4 py-2 bg-zinc-800 rounded-sm text-sm"
                    >
                      <Target className="w-4 h-4 text-teal-400" />
                      {area}
                    </span>
                  ))}
                </div>
              </section>
            )}

            {classData.equipment_needed?.length > 0 && (
              <section>
                <h2 className="text-2xl font-bold mb-4">EQUIPMENT NEEDED</h2>
                <div className="flex flex-wrap gap-3">
                  {classData.equipment_needed.map((item, index) => (
                    <span 
                      key={index}
                      className="flex items-center gap-2 px-4 py-2 bg-zinc-800 rounded-sm text-sm"
                    >
                      <Dumbbell className="w-4 h-4 text-orange-500" />
                      {item}
                    </span>
                  ))}
                </div>
              </section>
            )}
          </div>

          {/* Sidebar */}
          <div className="space-y-6">
            {/* Pre-Class Checklist */}
            <div className="bg-[#121212] border border-zinc-800 rounded-sm p-6">
              <h3 className="font-bold mb-4">PRE-CLASS CHECKLIST</h3>
              <ul className="space-y-3">
                <li className="flex items-start gap-3 text-sm text-zinc-400">
                  <Check className="w-4 h-4 text-green-500 mt-0.5 flex-shrink-0" />
                  <span>Ensure your Megaformer is set up with proper spring resistance</span>
                </li>
                <li className="flex items-start gap-3 text-sm text-zinc-400">
                  <Check className="w-4 h-4 text-green-500 mt-0.5 flex-shrink-0" />
                  <span>Have a towel and water nearby</span>
                </li>
                <li className="flex items-start gap-3 text-sm text-zinc-400">
                  <Check className="w-4 h-4 text-green-500 mt-0.5 flex-shrink-0" />
                  <span>Position your device where you can see the instructor</span>
                </li>
                <li className="flex items-start gap-3 text-sm text-zinc-400">
                  <Check className="w-4 h-4 text-green-500 mt-0.5 flex-shrink-0" />
                  <span>Warm up your body with light movement</span>
                </li>
              </ul>
            </div>

            {/* Safety Notice */}
            <div className="bg-orange-500/10 border border-orange-500/30 rounded-sm p-6">
              <h3 className="font-bold text-orange-500 mb-2">SAFETY NOTICE</h3>
              <p className="text-sm text-zinc-400">
                Listen to your body and modify movements as needed. If you experience any pain or discomfort, stop immediately. Consult your physician before starting any new exercise program.
              </p>
            </div>

            {/* Class Stats */}
            <div className="bg-[#121212] border border-zinc-800 rounded-sm p-6">
              <h3 className="font-bold mb-4">CLASS DETAILS</h3>
              <div className="space-y-4">
                <div className="flex justify-between">
                  <span className="text-zinc-500">Duration</span>
                  <span className="font-medium">{classData.duration_minutes} min</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-zinc-500">Difficulty</span>
                  <span className={`font-medium difficulty-${classData.difficulty}`}>
                    {classData.difficulty}
                  </span>
                </div>
                <div className="flex justify-between">
                  <span className="text-zinc-500">Category</span>
                  <span className="font-medium">{classData.category.replace('_', ' ')}</span>
                </div>
                {classData.total_ratings > 0 && (
                  <div className="flex justify-between">
                    <span className="text-zinc-500">Total Reviews</span>
                    <span className="font-medium">{classData.total_ratings}</span>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ClassDetail;
